import { useEffect, useRef } from 'react';
import {
  Copy,
  PencilLine,
  RotateCw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { cn } from '@/shared/cn';
import { CitationChip } from '@/design-system/CitationChip';
import { Commentable } from '@/shared/comments/Commentable';
import { useDrawer } from '@/app/providers';
import { getCitationSource } from '@/versions/v1/shared/citation-registry';
import type { ChatCitation, ChatMessage, ChatMessagePart } from '@/versions/v1/shared/chat-types';
import type { MetDiagnosticsSection } from '@/versions/v1/schema/met-diagnostics-cause';
import { useChat } from '@/versions/v1/shared/ChatContext';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { pbRecoveryRetrieval } from '@/versions/v1/shared/rule-set-retrieval';
import { CompactConversationNotice } from './ThreadNoticeRail';

const USER_ACTION_BUTTON_CLASS =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-[color-mix(in_srgb,var(--color-zinc-900)_5%,transparent)] hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-white/5 dark:hover:text-zinc-100';

export function ChatMessages() {
  const {
    messages,
    compactedEarlierCount,
    thinking,
    setDraft,
    setEditingMessageId,
    setEditDraft,
    resend,
  } = useChat();
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const visibleMessages =
    compactedEarlierCount > 0 ? messages.slice(compactedEarlierCount) : messages;

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, thinking, compactedEarlierCount]);

  return (
    <Commentable name="Messages">
      <div
        ref={bodyRef}
        className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto"
      >
        <div className="mx-auto w-full max-w-[var(--thread-max-width)] px-4 py-3">
          <CompactConversationNotice count={compactedEarlierCount} />
          {visibleMessages.map((m) =>
            m.who === 'user' ? (
              <UserMessage
                key={m.id}
                message={m}
                onEdit={() => {
                  setEditingMessageId(m.id);
                  setEditDraft(m.body);
                  setDraft('');
                }}
                onResend={() => resend(m.body)}
              />
            ) : (
              <AssistantMessage key={m.id} message={m} />
            ),
          )}
          {thinking ? <ThinkingLine /> : null}
        </div>
      </div>
    </Commentable>
  );
}

function UserMessage({
  message,
  onEdit,
  onResend,
}: {
  message: ChatMessage;
  onEdit: () => void;
  onResend: () => void;
}) {
  const copy = () => {
    void navigator.clipboard.writeText(message.body);
  };

  return (
    <div
      className={cn(
        'group/message mx-auto w-full max-w-[var(--thread-max-width)] px-0 pt-4 pb-9',
        'grid grid-cols-[auto_minmax(0,1fr)] gap-x-4',
        '[animation:message-in_320ms_cubic-bezier(0.22,0.61,0.36,1)_both] motion-reduce:animate-none',
      )}
    >
      <div className="col-start-2 flex justify-end">
        <div className="relative flex max-w-[80%] flex-col items-end gap-2">
          <div className="w-full max-w-[80%] rounded-2xl border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] px-3 py-2.5 backdrop-blur-md text-sm text-zinc-900 dark:text-zinc-100">
            {message.body}
          </div>
          <div className="absolute right-0 top-full mt-1 flex w-max items-center gap-1 whitespace-nowrap opacity-0 transition-opacity group-hover/message:opacity-100">
            <span className="whitespace-nowrap text-xs tabular-nums text-zinc-400 dark:text-zinc-500">
              {message.time}
            </span>
            <button
              type="button"
              className={USER_ACTION_BUTTON_CLASS}
              title="Copy"
              aria-label="Copy"
              onClick={copy}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className={USER_ACTION_BUTTON_CLASS}
              title="Edit message"
              aria-label="Edit message"
              onClick={onEdit}
            >
              <PencilLine className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className={USER_ACTION_BUTTON_CLASS}
              title="Resend this prompt"
              aria-label="Resend this prompt"
              onClick={onResend}
            >
              <RotateCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssistantMessage({ message }: { message: ChatMessage }) {
  const { open: openDrawer } = useDrawer();

  const copy = () => {
    void navigator.clipboard.writeText(message.body);
  };

  return (
    <div
      className={cn(
        'group/message mx-auto w-full max-w-[var(--thread-max-width)] px-0 py-4',
        '[animation:message-in_320ms_cubic-bezier(0.22,0.61,0.36,1)_both] motion-reduce:animate-none',
      )}
    >
      <div className="min-w-0">
        {message.parts?.length ? (
          <div className="space-y-3">
            {message.parts.map((part, index) => (
              <AssistantPart key={`${message.id}-${index}`} part={part} />
            ))}
          </div>
        ) : (
          <div className="text-sm text-zinc-900 dark:text-zinc-100">{message.body}</div>
        )}
        {message.citations && message.citations.length > 0 ? (
          <div className="mt-2">
            {message.citations.map((c, i) => {
              const id = c.id ?? c.label.split(/\s+§/)[0];
              const source = getCitationSource(id);
              return (
                <CitationChip
                  key={i}
                  label={c.label}
                  onClick={() =>
                    openDrawer('citation', {
                      kicker: 'CITATION',
                      title: source?.title ?? c.label,
                      data: { citationId: id },
                    })
                  }
                />
              );
            })}
          </div>
        ) : null}
        <div className="mt-1 flex items-center gap-1 opacity-0 transition-opacity group-hover/message:opacity-100">
          <button
            type="button"
            className={USER_ACTION_BUTTON_CLASS}
            title="Copy"
            aria-label="Copy"
            onClick={copy}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button type="button" className={USER_ACTION_BUTTON_CLASS} title="Good response" aria-label="Good response">
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>
          <button type="button" className={USER_ACTION_BUTTON_CLASS} title="Bad response" aria-label="Bad response">
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ThinkingLine() {
  return (
    <div className="flex items-center gap-2 py-2 text-sm italic text-zinc-400 dark:text-zinc-500">
      <Sparkles className="h-4 w-4 animate-spin" aria-hidden />
      Thinking…
    </div>
  );
}

function AssistantPart({ part }: { part: ChatMessagePart }) {
  if (part.type === 'rule-set-retrieval') {
    return <RuleSetRetrievalPart section={part.section} />;
  }

  if (part.type === 'tool') {
    return (
      <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-3 py-2 text-sm">
        <div className="font-medium text-zinc-900 dark:text-zinc-100">
          {part.verb}
        </div>
        <div className="text-muted">{part.preview}</div>
        {part.detail ? <div className="mt-1 text-xs text-muted">{part.detail}</div> : null}
      </div>
    );
  }

  if (part.type === 'artifact') {
    return (
      <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 shadow-sm">
        <div className="text-xs uppercase tracking-wide text-muted">{part.kind}</div>
        <div className="mt-1 font-medium text-zinc-900 dark:text-zinc-100">
          {part.title}
        </div>
      </div>
    );
  }

  return (
    <div className="whitespace-pre-wrap text-sm text-zinc-900 dark:text-zinc-100">
      {part.content.replace(/\*\*(.*?)\*\*/g, '$1')}
    </div>
  );
}

const CHECK_MARK_TONE: Record<'covered' | 'proposed' | 'gap', string> = {
  covered: 'text-emerald-600 dark:text-emerald-400',
  proposed: 'text-amber-600 dark:text-amber-400',
  gap: 'text-zinc-300 dark:text-zinc-600',
};

/** Direct-retrieval rule-set answer (#273, paul-fmea.A6) — computed at
 * render time from the live `AdvisorySessionState` (review MUST-FIX M4), and
 * rendered as structured rows rather than a `white-space: pre-wrap`
 * markdown block, whose two-space indentation collapses at rail width
 * (review SHOULD S2). */
function RuleSetRetrievalPart({ section }: { section: MetDiagnosticsSection }) {
  const advisory = useAdvisory();
  const { open: openDrawer } = useDrawer();
  const retrieval = pbRecoveryRetrieval(advisory);
  void section; // pbRecoveryRetrieval is scoped to Pb Recovery Below Target only (#273's brief); the field is carried on the part for a future multi-section retrieval thread

  return (
    <div className="space-y-2">
      <p className="text-sm text-zinc-900 dark:text-zinc-100">{retrieval.heading}</p>
      <ul className="divide-y divide-[var(--glass-border)] overflow-hidden rounded-lg border border-[var(--glass-border)]">
        {retrieval.checks.map((row) => (
          <li
            key={row.causeId}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-2.5 py-1.5 text-sm"
          >
            <span
              className={cn('w-3 shrink-0 text-center font-mono', CHECK_MARK_TONE[row.state])}
              aria-hidden
            >
              {row.state === 'covered' ? '✓' : row.state === 'proposed' ? '~' : '·'}
            </span>
            <span className="shrink-0 font-mono text-xs text-muted">{row.causeId}</span>
            <span className="min-w-0 flex-1 text-zinc-800 dark:text-zinc-200">{row.cause}</span>
            {row.rules.length > 0 ? (
              <span className="text-xs text-muted">
                → {row.rules.map((r) => r.id).join(', ')}
                {row.state === 'proposed' ? ' (proposed, not live)' : ''}
              </span>
            ) : (
              <span className="text-xs italic text-muted">gap</span>
            )}
          </li>
        ))}
      </ul>
      <p className="text-sm text-zinc-900 dark:text-zinc-100">{retrieval.closingLine}</p>
      {retrieval.citations.length > 0 ? (
        <RuleSetCitationChips citations={retrieval.citations} openDrawer={openDrawer} />
      ) : null}
    </div>
  );
}

function RuleSetCitationChips({
  citations,
  openDrawer,
}: {
  citations: readonly ChatCitation[];
  openDrawer: ReturnType<typeof useDrawer>['open'];
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {citations.map((c) => {
        const id = c.id ?? c.label;
        const source = getCitationSource(id);
        return (
          <CitationChip
            key={id}
            label={c.label}
            onClick={() =>
              openDrawer('citation', {
                kicker: 'CITATION',
                title: source?.title ?? c.label,
                data: { citationId: id },
              })
            }
          />
        );
      })}
    </div>
  );
}
